import React, {useEffect} from 'react';
import { EntityId } from "@reduxjs/toolkit";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { destroyComment, fetchReplies, updateComment } from "../../../../actions/mine_craft/commentsActions";
import {
  clearPreloadReplies,
  clearReplies,
  CommentInterface,
  selectCommentEntities,
  selectCommentIds,
  setEditId
} from "../../../../store/mine_craft/commentsSlice";
import { selectUserEntities, UserInterface } from "../../../../store/mine_craft/usersSlice";
import { formatterToUnits } from "../../../../helpers/NumberHelper";
import usePermissions from "../../../../hooks/usePermissions";

import { message, Space, Spin } from "antd";
import { Button, ButtonDestroy, ButtonEdit } from "../../../../componets/Buttons";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  CommentOutlined, EyeInvisibleOutlined, EyeOutlined,
  LikeOutlined
} from "@ant-design/icons";
import Comment from "../../../../componets/Comment";
import Title from "../../Users/_partials/_Title";

const LIMIT = 10;

const _List = () => {
  const dispatch        = useAppDispatch();
  const commentIds      = useAppSelector(selectCommentIds);
  const comments        = useAppSelector(selectCommentEntities);
  const users           = useAppSelector(selectUserEntities);
  const error           = useAppSelector(state => state.mineCraft.comments.error);
  const status          = useAppSelector(state => state.mineCraft.comments.status);
  const preloadReplies  = useAppSelector(state => state.mineCraft.comments.preloadReplies);
  const { write_minecraft } = usePermissions();

  useEffect(() => {
    if (!preloadReplies.length || status !== 'succeeded') {
      return;
    }

    (async () => {
      for (const reply of preloadReplies) {
        const { comment_id, offset }: any = reply;
        const params: any = {
          limit: 3,
          order: 'desc',
          order_by: 'id',
          offset: (offset - 2) > 0  ? offset - 2 : 0,
          comment_id: comment_id
        };

        await dispatch(fetchReplies(params));
      }
    })();

    dispatch(clearPreloadReplies());
  }, [preloadReplies, dispatch, status]);

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  function onClickDestroy(id: EntityId) {
    handleDestroy(id).catch(console.error);
  }

  function onClickReplies(id: EntityId) {
    const comment = comments[id];

    if (!comment || comment.comments.length > 0) {
      return;
    }

    const params: any = {
      limit: LIMIT,
      order: 'desc',
      order_by: 'id',
      comment_id: id
    };

    dispatch(fetchReplies(params))
  }
  function onClickLoadMore(id: EntityId, offsetId?: EntityId) {
    const params: any = {
      limit: LIMIT,
      order: 'desc',
      order_by: 'id',
      comment_id: id
    };

    if (offsetId) {
      params.offset_id = offsetId;
    }

    dispatch(fetchReplies(params))
  }

  function onClickLoadLatest(id: EntityId, offsetId?: EntityId) {
    const params: any = {
      limit: LIMIT,
      order: 'asc',
      order_by: 'id',
      comment_id: id
    };

    if (offsetId) {
      params.offset_id = offsetId;
    }

    dispatch(fetchReplies(params))
  }

  function onClickCloseReplies(id: EntityId) {
    dispatch(clearReplies(id))
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyComment(id));

    if (destroyComment.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
    }

    if (destroyComment.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  function toggleHide(comment: CommentInterface) {
    dispatch(updateComment({ id: comment.id, data: { hidden: !comment.hidden } }));
  }

  const ShowHideButton = ({ comment }: { comment: CommentInterface }) => {
    if (comment.hidden) {
      return <Button icon={EyeOutlined} title='Show' onClick={() => toggleHide(comment)} />
    } else {
      return <Button icon={EyeInvisibleOutlined} title='Hide' onClick={() => toggleHide(comment)} />
    }
  }

  const renderAuthor = (user: UserInterface | undefined) => {
    if (!user) {
      return null;
    }

    return (
      <Link to={user.url}>
        <Title name={user.name} bannedUntil={user.banned_until} mutedUntil={user.muted_until} />
      </Link>
    );
  }

  const renderReplies = (comment: CommentInterface) => {
    if (!comment.comments.length) {
      return null;
    }

    const firstId: any = comment.comments[0];
    const lastId: any = comment.comments[comment.comments.length - 1];

    return (
      <>
        <Space className='v-mb-10' size="middle">
          <Button icon={ArrowUpOutlined} onClick={() => onClickLoadLatest(comment.id, firstId)} title='Load latest' />
          <Button icon={CloseOutlined} onClick={() => onClickCloseReplies(comment.id)} title='Close thread' />
        </Space>

        {comment.comments.map(renderComment)}

        <Space className='v-mb-10' size="middle">
          <Button icon={ArrowDownOutlined} onClick={() => onClickLoadMore(comment.id, lastId)} title='Load more' />
          <Button icon={CloseOutlined} onClick={() => onClickCloseReplies(comment.id)} title='Close thread' />
        </Space>
      </>
    )
  }

  const renderComment = (id: EntityId) => {
    const comment = comments[id];

    if (!comment) {
      return null;
    }

    const user    = users[comment?.sender_id];
    const likes   = formatterToUnits(comment.likes_count, 1);
    const replies = formatterToUnits(comment.comments_count, 1);

    const actions = [
      ...(write_minecraft ? [
        <ButtonEdit key="Edit" onClick={() => onClickEdit(id)} />,
        <ShowHideButton comment={comment} />,
        <ButtonDestroy key="Destroy" onClick={() => onClickDestroy(id)} />,
      ] : []),
      <Button onClick={() => {}} icon={LikeOutlined} title={likes} />,
      <Button onClick={() => onClickReplies(id)} icon={CommentOutlined} title={`Replies ${replies}`} />,
    ];

    return (
      <Comment
        key={id}
        id={comment.id}
        body={comment.body}
        hidden={comment.hidden}
        author={renderAuthor(user)}
        avatar_url={user?.avatar_url}
        createdAt={comment.created_at}
        actions={actions}
      >
        {renderReplies(comment)}
      </Comment>
    )
  }

  if (status === 'loading') {
    return <Spin/>;
  } else if (status === 'failed') {
    return <div>Error: {error}</div>
  }

  return (
    <div>
      {commentIds.map(renderComment)}
    </div>
  )
}

export default _List;
